import React from 'react';
import Swal from 'sweetalert2';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { getRequest, postRequest } from 'Includes/api/apicall';
import { GET_URL, POST_URL } from 'Includes/urls';
import { Actions, screenTypes } from 'Constants/permissions';

const MENU_TYPE_OPTIONS = [
  { value: 'web', label: 'Web' },
  { value: 'app', label: 'App' },
  { value: 'staff_app', label: 'Staff App' },
];

const menuTypeForCode = (codename) => {
  const code = codename || '';
  if (code.startsWith('staff_app')) return 'staff_app';
  if (code.startsWith('visible')) return 'web';
  if (code.startsWith('app')) return 'app';
  return '';
};

const menuLabel = (menuType) => (
  MENU_TYPE_OPTIONS.find((option) => option.value === menuType)?.label || menuType || '-'
);

const schoolLabel = (school) => school?.name || school?.database_name || school?.database_key || 'School';

const unwrapDetail = (response) => {
  const body = response?.data;
  if (body == null) return {};
  if (body.data != null) return body.data;
  return body;
};

const catalogFromActions = () => {
  const rows = [];
  const seen = new Set();
  Object.values(Actions).forEach((action) => {
    if (!action || typeof action !== 'object') return;
    screenTypes.forEach((type) => {
      const block = action[type];
      const code = block?.action_code;
      if (!block?.permission_needed || !code || seen.has(code)) return;
      const menuType = menuTypeForCode(code);
      if (!menuType) return;
      seen.add(code);
      rows.push({
        codename: code,
        name: block.name || action.name || code,
        menu_type: menuType,
      });
    });
  });
  return rows;
};

function EdubriczPermission(props) {
  const [groups, setGroups] = React.useState([]);
  const [schools, setSchools] = React.useState([]);
  const [catalog, setCatalog] = React.useState([]);
  const [selectedGroup, setSelectedGroup] = React.useState(null);
  const [selectedSchools, setSelectedSchools] = React.useState([]);
  const [selectedCodes, setSelectedCodes] = React.useState([]);
  const [menuType, setMenuType] = React.useState('web');
  const [search, setSearch] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [loadingGroup, setLoadingGroup] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'error' });
  const syncedRoles = React.useRef([]);

  const showMessage = (message, severity = 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  React.useEffect(() => {
    setLoading(true);
    Promise.all([
      getRequest(GET_URL.groups.api, {}, props),
      getRequest(GET_URL.client_track_report.api, { school_options: 1 }, props),
      getRequest(GET_URL.client_track_report.api, { roles_permissions: 1 }, props),
    ]).then(([groupResponse, schoolResponse, roleResponse]) => {
      const signupGroups = Array.isArray(groupResponse?.data?.data) ? groupResponse.data.data : [];
      const schoolRows = Array.isArray(schoolResponse?.data?.data) ? schoolResponse.data.data : [];
      const roleRows = Array.isArray(roleResponse?.data?.data) ? roleResponse.data.data : [];
      syncedRoles.current = roleRows;

      const byName = new Map();
      signupGroups.forEach((group) => {
        const name = (group?.name || '').trim();
        if (!name || byName.has(name)) return;
        byName.set(name, { id: group.id, name });
      });
      roleRows.forEach((school) => {
        (school.roles || []).forEach((role) => {
          const name = (role?.name || '').trim();
          if (!name || byName.has(name)) return;
          byName.set(name, { id: null, name });
        });
      });

      const permissionByCode = new Map();
      catalogFromActions().forEach((permission) => {
        permissionByCode.set(permission.codename, permission);
      });
      roleRows.forEach((school) => {
        (school.roles || []).forEach((role) => {
          (role.permissions || []).forEach((permission) => {
            const code = permission?.codename;
            if (!code || permissionByCode.has(code)) return;
            const type = permission.menu_type || menuTypeForCode(code);
            if (!type) return;
            permissionByCode.set(code, {
              codename: code,
              name: permission.name || code,
              menu_type: type,
            });
          });
        });
      });

      const nextGroups = Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
      const nextCatalog = Array.from(permissionByCode.values()).sort((a, b) => (
        (a.name || '').localeCompare(b.name || '')
      ));
      setGroups(nextGroups);
      setSchools(schoolRows);
      setCatalog(nextCatalog);
    }).finally(() => setLoading(false));
  }, [props]);

  const groupRequest = React.useRef('');

  const applyRolePermissions = React.useCallback((groupName, codes) => {
    if (groupRequest.current !== groupName) return;
    if (codes.length) {
      setSelectedCodes(codes);
      return;
    }
    const match = syncedRoles.current.find((school) => (
      (school.roles || []).some((role) => role?.name === groupName)
    ));
    const role = (match?.roles || []).find((item) => item?.name === groupName);
    setSelectedCodes((role?.permissions || []).map((permission) => permission.codename).filter(Boolean));
  }, []);

  const handleGroupChange = (_, group) => {
    setSelectedGroup(group);
    setPage(0);
    groupRequest.current = group?.name || '';
    if (!group) {
      setSelectedCodes([]);
      setLoadingGroup(false);
      return;
    }
    if (!group.id) {
      applyRolePermissions(group.name, []);
      return;
    }
    setLoadingGroup(true);
    getRequest(`${GET_URL.groups.api}${group.id}/`, {}, props)
      .then((response) => {
        if (groupRequest.current !== group.name) return;
        if (response?.status !== 200) {
          applyRolePermissions(group.name, []);
          return;
        }
        const payload = unwrapDetail(response);
        const permissions = payload?.group?.permissions || payload?.permissions || [];
        const codes = permissions
          .map((permission) => permission?.codename || permission?.code)
          .filter(Boolean);
        applyRolePermissions(group.name, codes);
      })
      .finally(() => {
        if (groupRequest.current === group.name) setLoadingGroup(false);
      });
  };

  const filteredPermissions = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    return catalog.filter((permission) => {
      if (permission.menu_type !== menuType) return false;
      if (!query) return true;
      return (
        (permission.name || '').toLowerCase().includes(query)
        || (permission.codename || '').toLowerCase().includes(query)
      );
    });
  }, [catalog, menuType, search]);

  const pageRows = filteredPermissions.slice(page * pageSize, page * pageSize + pageSize);
  const selectedCodeSet = React.useMemo(() => new Set(selectedCodes), [selectedCodes]);
  const selectedInMenu = catalog.filter((permission) => (
    permission.menu_type === menuType && selectedCodeSet.has(permission.codename)
  )).length;
  const selectedInFilter = filteredPermissions.filter((permission) => selectedCodeSet.has(permission.codename)).length;
  const allFilteredChecked = filteredPermissions.length > 0 && selectedInFilter === filteredPermissions.length;

  const toggleCode = (codename) => {
    setSelectedCodes((current) => (
      current.includes(codename)
        ? current.filter((code) => code !== codename)
        : current.concat(codename)
    ));
  };

  const toggleFiltered = () => {
    const filteredCodes = filteredPermissions.map((permission) => permission.codename);
    setSelectedCodes((current) => {
      if (allFilteredChecked) {
        const remove = new Set(filteredCodes);
        return current.filter((code) => !remove.has(code));
      }
      return Array.from(new Set(current.concat(filteredCodes)));
    });
  };

  const permissionsForSubmit = () => {
    const chosen = new Set(selectedCodes);
    const rows = catalog.filter((permission) => (
      chosen.has(permission.codename) && permission.menu_type === menuType
    ));
    const listed = new Set(rows.map((permission) => permission.codename));
    selectedCodes.forEach((code) => {
      if (listed.has(code) || menuTypeForCode(code) !== menuType) return;
      rows.push({ codename: code, name: code, menu_type: menuType });
    });
    return rows.map((permission) => ({
      codename: permission.codename,
      name: permission.name || permission.codename,
    }));
  };

  const sendUpdate = (permissions) => {
    setSubmitting(true);
    postRequest(POST_URL.client_track_report.api, {
      update_group_permissions: 1,
      group_name: selectedGroup.name,
      menu_type: menuType,
      permissions,
      database_keys: selectedSchools.map((school) => school.database_key).filter(Boolean),
    }, { ...props, return_error: true }).then((response) => {
      if (response && (response.status === 200 || response.status === 201)) {
        Swal.fire({
          position: 'top-end',
          icon: 'success',
          title: response.data?.Reason || 'Updating permissions in Edubricz',
          showConfirmButton: false,
          timer: 2200,
        });
        return;
      }
      let reason = response?.data?.Reason || response?.data?.detail || 'Could not update permissions in Edubricz.';
      if (Array.isArray(reason)) reason = reason.join(' ');
      if (reason && typeof reason === 'object') {
        reason = Object.values(reason).flat().join(' ');
      }
      showMessage(typeof reason === 'string' && reason ? reason : 'Could not update permissions in Edubricz.');
    }).finally(() => setSubmitting(false));
  };

  const submit = () => {
    if (!selectedGroup?.name) {
      showMessage('Select a group.');
      return;
    }
    if (selectedSchools.length === 0) {
      showMessage('Select at least one school.');
      return;
    }
    const permissions = permissionsForSubmit();
    if (permissions.length === 0) {
      Swal.fire({
        title: 'Remove these permissions?',
        text: `${menuLabel(menuType)} permissions for ${selectedGroup.name} will be cleared on the selected schools.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, update',
      }).then((result) => {
        if (result.isConfirmed || result.value) sendUpdate([]);
      });
      return;
    }
    sendUpdate(permissions);
  };

  return (
    <Box p={2}>
      <Typography variant="h6" fontWeight={700}>Edubricz Permission</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select a group and its permissions, then the schools to update. Submit writes that menu onto the same group in each selected school.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={groups}
                value={selectedGroup}
                onChange={handleGroupChange}
                getOptionLabel={(group) => group?.name || ''}
                isOptionEqualToValue={(option, value) => option?.name === value?.name}
                renderInput={(params) => <TextField {...params} label="Group" size="small" />}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                limitTags={3}
                options={schools}
                value={selectedSchools}
                onChange={(_, value) => setSelectedSchools(value)}
                getOptionLabel={schoolLabel}
                isOptionEqualToValue={(option, value) => option?.database_key === value?.database_key}
                renderInput={(params) => <TextField {...params} label="Schools" size="small" placeholder="Select schools" />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Menu"
                value={menuType}
                onChange={(event) => {
                  setMenuType(event.target.value);
                  setPage(0);
                }}
              >
                {MENU_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                label="Search permission"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={12} md={4} display="flex" alignItems="center" gap={1}>
              <Button size="small" onClick={toggleFiltered} disabled={filteredPermissions.length === 0}>
                {allFilteredChecked ? 'Clear filtered' : 'Select filtered'}
              </Button>
            </Grid>
          </Grid>

          <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
            <Chip label={`${selectedInMenu} selected in ${menuLabel(menuType)}`} color="primary" variant="outlined" />
            <Chip label={`${selectedSchools.length} schools`} />
            <Chip label={`${filteredPermissions.length} permissions`} />
          </Box>

          {loadingGroup ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allFilteredChecked}
                        indeterminate={!allFilteredChecked && selectedInFilter > 0}
                        onChange={toggleFiltered}
                        disabled={filteredPermissions.length === 0}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Permission</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Menu</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!selectedGroup ? (
                    <TableRow>
                      <TableCell colSpan={4}>Select a group to choose permissions.</TableCell>
                    </TableRow>
                  ) : pageRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>No permissions for this menu.</TableCell>
                    </TableRow>
                  ) : pageRows.map((permission) => (
                    <TableRow
                      key={permission.codename}
                      hover
                      onClick={() => toggleCode(permission.codename)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={selectedCodeSet.has(permission.codename)}
                          onChange={() => toggleCode(permission.codename)}
                        />
                      </TableCell>
                      <TableCell>{permission.name || '-'}</TableCell>
                      <TableCell>{permission.codename}</TableCell>
                      <TableCell>{menuLabel(permission.menu_type)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={filteredPermissions.length}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(event) => {
                  setPageSize(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </TableContainer>
          )}

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              variant="contained"
              onClick={submit}
              disabled={submitting || !selectedGroup || selectedSchools.length === 0}
            >
              {submitting ? 'Updating...' : 'Submit'}
            </Button>
          </Box>
        </>
      )}

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((current) => ({ ...current, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default EdubriczPermission;
